// SPDX-License-Identifier: BUSL-1.1

import type React from 'react';
import { useState } from 'react';
import { Building2, Mail, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { showError, showSuccess } from '@/lib/toast';
import { createOrganization } from '@/lib/orgs';
import { fetchAndStoreBearerJwt } from '@/lib/auth';

export function OnboardingLobby() {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  function syncSlug(value: string) {
    setName(value);
    setSlug(
      value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 31),
    );
  }

  async function handleCreateWorkspace() {
    if (!name.trim() || !slug.trim()) {
      showError('Workspace name and slug are required');
      return;
    }

    setIsCreating(true);
    try {
      await createOrganization({ name: name.trim(), slug: slug.trim() });
      await fetchAndStoreBearerJwt({ forceRefresh: true });
      showSuccess('Workspace created');
      window.location.reload();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to create workspace');
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
      <Card className="w-full max-w-3xl border-emerald-900/40 bg-neutral-950/80">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-2 text-emerald-400">
            <Sparkles className="size-5" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Welcome to build.ceo</span>
          </div>
          <CardTitle className="text-3xl text-neutral-100">Create or join a workspace</CardTitle>
          <CardDescription className="max-w-2xl text-sm text-neutral-400">
            Your account is authenticated and ready, but you do not belong to a workspace yet.
            Choose how you want to get started. GitHub linking happens after a real workspace exists.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Card className="border-neutral-800 bg-neutral-900/70">
            <CardHeader>
              <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-emerald-950 text-emerald-400">
                <Building2 className="size-5" />
              </div>
              <CardTitle className="text-xl text-neutral-100">Create a workspace</CardTitle>
              <CardDescription className="text-neutral-400">
                Start a new workspace for your team. You’ll become the owner and can invite members afterward.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-3 space-y-3">
                <Input placeholder="Workspace name" value={name} onChange={(e) => syncSlug(e.target.value)} />
                <Input placeholder="workspace-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
              </div>
              <p className="mb-4 text-sm text-neutral-500">
                Best if you’re setting up build.ceo for a new team or project.
              </p>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-500" onClick={() => void handleCreateWorkspace()} disabled={isCreating}>
                {isCreating ? 'Creating workspace...' : 'Create workspace'}
              </Button>
            </CardContent>
          </Card>

          <Card className="border-neutral-800 bg-neutral-900/70">
            <CardHeader>
              <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-blue-950 text-blue-400">
                <Mail className="size-5" />
              </div>
              <CardTitle className="text-xl text-neutral-100">Join via invitation</CardTitle>
              <CardDescription className="text-neutral-400">
                Already invited to a workspace? Open the invitation link from your email while signed in.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-neutral-500">
                Ask a workspace owner to send you an invitation if you should join an existing team.
              </p>
              <div className="rounded border border-neutral-800 bg-neutral-950/60 px-4 py-3 text-sm text-neutral-300">
                Invitation links will take you directly into the correct workspace once accepted.
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}
